import { useMutation } from "@tanstack/react-query";
import {
  endAskHelenaVoiceSession,
  endAskHelenaVoiceSessionOnUnload,
  startAskHelenaVoiceSession,
  uploadAskHelenaVoiceAudio,
} from "controller/api/helena";
import { useCallback } from "react";
import type { ApiErrorType } from "shared";
import { sentryLogger } from "utilz";
import { debugConsole } from "utilz/helpers";

/**
 * Statuses the voice endpoints use as answers, not faults: dictation turned
 * off, the monthly minutes used up, the provider down, an upload already made,
 * storage not configured. The UI handles each; Sentry only hears the rest.
 */
const EXPECTED_VOICE_STATUSES = new Set([403, 409, 429, 502, 503]);

const logVoiceError = (error: ApiErrorType, functionName: string) => {
  debugConsole(error);
  if (EXPECTED_VOICE_STATUSES.has(error.status)) return;
  sentryLogger({ error, workflow: "helena", level: "error", functionName });
};

/** Mints the single-use key. Never retried: a retry is a second billed session. */
export const useStartAskHelenaVoiceSession = () => {
  return useMutation({
    mutationFn: startAskHelenaVoiceSession,
    onError: (error: ApiErrorType) =>
      logVoiceError(error, "startAskHelenaVoiceSession"),
  });
};

export const useEndAskHelenaVoiceSession = () => {
  return useMutation({
    mutationFn: endAskHelenaVoiceSession,
    onError: (error: ApiErrorType) =>
      logVoiceError(error, "endAskHelenaVoiceSession"),
  });
};

/**
 * The tab-close `/end`. A plain callback, not a mutation: it has to fire
 * synchronously inside `pagehide` with a keepalive fetch, and there is no page
 * left afterwards to hold mutation state or report an error to.
 */
export const useEndAskHelenaVoiceSessionOnUnload = () =>
  useCallback(
    (input: Parameters<typeof endAskHelenaVoiceSessionOnUnload>[0]) =>
      endAskHelenaVoiceSessionOnUnload(input),
    [],
  );

export const useUploadAskHelenaVoiceAudio = () => {
  return useMutation({
    mutationFn: uploadAskHelenaVoiceAudio,
    onError: (error: ApiErrorType) =>
      logVoiceError(error, "uploadAskHelenaVoiceAudio"),
  });
};
